import { useMutation } from '@vue/apollo-composable';

import {
  CreateClientFromConversationDocument,
  DeleteMessengerMessageDocument,
  LinkConversationClientDocument,
  MarkConversationAsReadDocument,
  MarkMessengerDialogAnsweredDocument,
  ReassignMessengerDialogDocument,
  ResolveMessengerDialogDocument,
  SendMessengerMessageDocument,
  SetConversationDefaultDocument,
  TakeMessengerDialogDocument,
  UnassignMessengerDialogDocument,
  UnlinkConversationClientDocument,
} from '@shared/api/graphql/gql/graphql';

import type {
  ConversationCreateClientResult,
  ConversationLinkResult,
  ConversationWarning,
  MessengerConversation,
  MessengerDialog,
  MessengerMessage,
} from '../model/types';

import { messengerQueryNames } from './messenger-query-names';

/**
 * Списки, которые надо перечитать после изменения привязки/основного диалога:
 * список групп диалогов мессенджера и переписки клиента (карточка клиента).
 */
const CONVERSATION_REFETCH = [
  messengerQueryNames.getMessengerConversationGroups,
  messengerQueryNames.getClientMessengerConversations,
];

/**
 * Mutation для операторской привязки диалога к клиенту (D4).
 * force=true — атомарная перепривязка с другого клиента.
 */
export const useLinkConversationToClientMutation = () => {
  const { mutate, loading } = useMutation(LinkConversationClientDocument, {
    refetchQueries: CONVERSATION_REFETCH,
  });

  const mutateAsync = async (variables: {
    conversationId: string;
    clientId: string;
    force?: boolean;
  }): Promise<ConversationLinkResult> => {
    const response = await mutate({
      conversationId: variables.conversationId,
      clientId: variables.clientId,
      force: variables.force ?? false,
    });

    const payload = response?.data?.linkConversationClient;
    const conversation = payload?.conversation;
    if (!conversation) {
      throw new Error('Не удалось привязать диалог к клиенту');
    }

    return {
      conversation,
      warnings: payload.warnings as ConversationWarning[],
    };
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для операторской отвязки диалога от клиента (D4)
 */
export const useUnlinkConversationFromClientMutation = () => {
  const { mutate, loading } = useMutation(UnlinkConversationClientDocument, {
    refetchQueries: CONVERSATION_REFETCH,
  });

  const mutateAsync = async (conversationId: string): Promise<MessengerConversation> => {
    const response = await mutate({ conversationId });
    const node = response?.data?.unlinkConversationClient;
    if (!node) {
      throw new Error('Не удалось отвязать диалог от клиента');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для создания клиента из диалога с дедупом по телефону (D-A)
 */
export const useCreateClientFromConversationMutation = () => {
  const { mutate, loading } = useMutation(CreateClientFromConversationDocument, {
    refetchQueries: CONVERSATION_REFETCH,
  });

  const mutateAsync = async (variables: {
    conversationId: string;
    firstName?: string;
    lastName?: string;
    phone: string;
  }): Promise<ConversationCreateClientResult> => {
    const response = await mutate({
      conversationId: variables.conversationId,
      input: {
        first_name: variables.firstName,
        last_name: variables.lastName,
        phone: variables.phone,
      },
    });

    const payload = response?.data?.createClientFromConversation;
    const conversation = payload?.conversation;
    if (!conversation) {
      throw new Error('Не удалось создать клиента из диалога');
    }

    return {
      conversation,
      existing: payload.existing,
      warnings: payload.warnings as ConversationWarning[],
    };
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для назначения диалога основным каналом связи клиента (R6)
 */
export const useSetConversationDefaultMutation = () => {
  const { mutate, loading } = useMutation(SetConversationDefaultDocument, {
    refetchQueries: CONVERSATION_REFETCH,
  });

  const mutateAsync = async (conversationId: string): Promise<MessengerConversation> => {
    const response = await mutate({ conversationId });
    const node = response?.data?.setConversationDefault;
    if (!node) {
      throw new Error('Не удалось назначить диалог основным');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Взять диалог в работу. Конфликт (dialog_already_taken) пробрасывается наружу —
 * вызывающий показывает toast и рефетчит.
 */
export const useTakeMessengerDialogMutation = () => {
  const { mutate, loading } = useMutation(TakeMessengerDialogDocument);

  const mutateAsync = async (dialogId: string): Promise<MessengerDialog> => {
    const response = await mutate({ dialogId });
    const node = response?.data?.takeMessengerDialog;
    if (!node) {
      throw new Error('Не удалось взять диалог в работу');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Закрыть диалог («Вопрос решён»). Доступно только текущему владельцу
 * (dialog_not_owned пробрасывается наружу).
 */
export const useResolveMessengerDialogMutation = () => {
  const { mutate, loading } = useMutation(ResolveMessengerDialogDocument);

  const mutateAsync = async (dialogId: string): Promise<MessengerDialog> => {
    const response = await mutate({ dialogId });
    const node = response?.data?.resolveMessengerDialog;
    if (!node) {
      throw new Error('Не удалось закрыть диалог');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Переназначить диалог другому оператору (в т.ч. перехват на себя).
 */
export const useReassignMessengerDialogMutation = () => {
  const { mutate, loading } = useMutation(ReassignMessengerDialogDocument);

  const mutateAsync = async (variables: { dialogId: string; employeeId: string }): Promise<MessengerDialog> => {
    const response = await mutate({
      dialogId: variables.dialogId,
      employeeId: variables.employeeId,
    });
    const node = response?.data?.reassignMessengerDialog;
    if (!node) {
      throw new Error('Не удалось переназначить диалог');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Снять оператора с диалога (с себя или с чужого). Идемпотентно на бэке; ответ —
 * фрагмент MessengerDialog, Apollo сам патчит нормализованный кэш по id.
 */
export const useUnassignMessengerDialogMutation = () => {
  const { mutate, loading } = useMutation(UnassignMessengerDialogDocument);

  const mutateAsync = async (dialogId: string): Promise<MessengerDialog> => {
    const response = await mutate({ dialogId });
    const node = response?.data?.unassignMessengerDialog;
    if (!node) {
      throw new Error('Не удалось снять диалог с оператора');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Пометить диалог отвеченным (triage, waiting_since → NULL). Идемпотентно на бэке;
 * ответ — фрагмент MessengerDialog, Apollo сам патчит нормализованный кэш по id.
 */
export const useMarkMessengerDialogAnsweredMutation = () => {
  const { mutate, loading } = useMutation(MarkMessengerDialogAnsweredDocument);

  const mutateAsync = async (dialogId: string): Promise<MessengerDialog> => {
    const response = await mutate({ dialogId });
    const node = response?.data?.markMessengerDialogAnswered;
    if (!node) {
      throw new Error('Не удалось пометить диалог отвеченным');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для отправки сообщения (GraphQL/Apollo, файл — Upload).
 * Без refetchQueries: и лента, и последнее сообщение группы обновляются в
 * Apollo-кэше оптимистично (composable) и по WS (upsert по id) — полный
 * рефетч списка групп был двойной работой (ревью 4.2).
 */
export const useSendMessageMutation = () => {
  const { mutate, loading } = useMutation(SendMessengerMessageDocument);

  const mutateAsync = async (variables: {
    conversationId: string;
    body: string;
    targetConversationId: string;
    file?: File;
  }): Promise<MessengerMessage> => {
    const response = await mutate({
      input: {
        conversationId: variables.conversationId,
        targetConversationId: variables.targetConversationId,
        body: variables.body || null,
        file: variables.file ?? null,
      },
    });

    const node = response?.data?.sendMessengerMessage;
    if (!node) {
      throw new Error('Не удалось отправить сообщение');
    }

    return node;
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для удаления сообщения (GraphQL/Apollo).
 * Без refetchQueries: удаление применяется в кэш ленты (composable) и по WS
 * `message.deleted` (обновляет и last_message группы) — рефетч был двойной работой.
 */
export const useDeleteMessageMutation = () => {
  const { mutate, loading } = useMutation(DeleteMessengerMessageDocument);

  const mutateAsync = async (messageId: string): Promise<void> => {
    await mutate({ id: messageId });
  };

  return { mutateAsync, isPending: loading };
};

/**
 * Mutation для пометки диалога прочитанным (входящие непрочитанные → read).
 * Вызывается при открытии диалога (query сообщений — чистая, D6).
 */
export const useMarkConversationAsReadMutation = () => {
  const { mutate, loading } = useMutation(MarkConversationAsReadDocument);

  const mutateAsync = async (conversationId: string): Promise<void> => {
    await mutate({ conversationId });
  };

  return { mutateAsync, isPending: loading };
};
